import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { User } from '../../models/User';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, Search } from 'lucide-react';
import { IconUserPlus } from './FigmaIcons';

interface UserSelectModalProps {
    show: boolean;
    title: string;
    users: User[];
    onClose: () => void;
    onSelect: (user: User) => void;
}

const UserSelectModal: React.FC<UserSelectModalProps> = ({ show, title, users, onClose, onSelect }) => {
    const { i18n } = useLingui();
    const [searchText, setSearchText] = useState('');

    const filteredUsers = useMemo(() => {
        if (!searchText) return users;
        return users.filter(user =>
            user.name.toLowerCase().includes(searchText.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [users, searchText]);

    return (
        <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md p-0 overflow-hidden flex flex-col max-h-[80vh]">
                <DialogHeader className="p-4 border-b">
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                {/* Search - Sticky */}
                <div className="p-4 bg-background border-b border-border sticky top-0 z-10 shrink-0">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder={t`Search members`}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="pl-9 bg-transparent border-input focus-visible:ring-primary focus-visible:border-primary"
                        />
                    </div>
                </div>

                {/* List */}
                <div className="overflow-y-auto pb-4 px-2 pt-0 flex-1">
                    {filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            {t`No members found`}
                        </div>
                    ) : (
                        <ul className="space-y-1">
                            {filteredUsers.map(user => (
                                <li key={user.id} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-lg transition-colors group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="h-10 w-10 rounded-full bg-muted overflow-hidden flex-shrink-0">
                                            {user.photoUrl ? (
                                                <img src={user.photoUrl} alt={user.name} className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center bg-primary/20 text-primary font-bold text-sm">
                                                    {user.name.charAt(0)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium text-[color:rgb(var(--text-body))] truncate">{user.name}</span>
                                            <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="default"
                                        onClick={() => onSelect(user)}
                                        className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm h-8 px-3"
                                    >
                                        <IconUserPlus className="h-4 w-4 mr-1.5" />
                                        {t`Invite`}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default UserSelectModal;
